/**
 * The investor-relations host (A7Y-4409): the deck lives at
 * `ir.apptonomy.ai/era/seed/`, with `ir-1.` (staging) and `ir-sandbox.`
 * siblings beside the matching `www-*` hosts. Shared by the worker (routing, redirects,
 * deck-load tagging) and the SPA (which refuses to mount the admin app there).
 */

/** The seed round's directory on the ir host — it has no page of its own. */
export const SEED_ROUND_PATH = '/era/seed/';

/** Canonical path of the seed-round deck on the ir host. */
export const SEED_DECK_PATH = `${SEED_ROUND_PATH}series-seed-primer`;

/**
 * Where the deck lives since 2026-10-07 (TPF): ir.apptonomy.ai is a Papermark
 * custom domain serving the deck PDF, no longer this Worker. Every pre-move
 * production link answers 301 here, and so does every staging ir link since
 * 2026-10-26 (A7Y-5127); only the sandbox ir host serves the web deck, for
 * previews.
 */
export const PAPERMARK_DECK_URL = 'https://ir.apptonomy.ai/pre-seed';
export const PRODUCTION_INVESTOR_HOST = 'ir.apptonomy.ai';

/** The ir host's label beside each www host's environment suffix. */
const IR_LABEL_BY_WWW_SUFFIX: Record<string, string> = {
  '': 'ir',
  '-staging': 'ir-1',
  '-sandbox': 'ir-sandbox',
};

/** True for ir.apptonomy.ai and its staging/sandbox siblings. */
export function isInvestorHost(hostname: string): boolean {
  return /^ir(-1|-sandbox)?\./.test(hostname);
}

/**
 * The staging ir hosts — `ir-1.` and its pre-rename `ir-staging.` — retired
 * 2026-10-26 (A7Y-5127, TPF): the links investors hold point there, and every
 * request answers 301 to the Papermark deck.
 */
export function isRetiredInvestorHost(hostname: string): boolean {
  return /^ir-(1|staging)\./.test(hostname);
}

/** www[-env].apptonomy.ai → its ir sibling; null for any other host. */
export function investorHostFor(hostname: string): string | null {
  const match = hostname.match(/^www(-staging|-sandbox)?\.(.+)$/);
  return match ? `${IR_LABEL_BY_WWW_SUFFIX[match[1] ?? '']}.${match[2]}` : null;
}

/**
 * Where a pre-move `/capital/...` path lives on the ir host, so links already
 * sent to investors keep working: the deck (and any pinned version) maps onto
 * `/era/seed/series-seed-primer`, the share card onto its new home, anything
 * else onto the deck.
 */
export function investorPathFor(capitalPath: string): string {
  const deck = capitalPath.match(/^\/capital\/series-seed-primer(\/.*)?$/);
  if (deck) return deck[1] && deck[1] !== '/' ? `${SEED_DECK_PATH}${deck[1]}` : SEED_DECK_PATH;
  const card = capitalPath.match(/^\/capital\/og\/(.+)$/);
  if (card) return `${SEED_ROUND_PATH}og/${card[1]}`;
  return SEED_DECK_PATH;
}
